import { take, takeEvery, put, select, putResolve } from "redux-saga/effects";

import { closeModal, isModalOpened, openModal } from "libs/modals";
import { Post } from "../types";
import { closePostForm } from "../../../actions/postForm";
import { ReduxRootState } from "../../../types/redux-store";
import { AccountType } from "../../../shared/types/accounts";
import {
  generateNewPostId,
  standardizationPostData,
  standardizationUpcomingPosts,
} from "../../../utils/posts";
import { createPostNormalizer } from "../components/create-post-normalizer";
import { PostFormState } from "../../../shared/types/post";
import { savePostForm } from "../../../sagas/postsSagas";
import axios from "axios";
import {
  LoadedDraft,
  normalizeDraft,
} from "../../../selectors/loaded-posts-normalizer";
import { isFormSubmitting, postsCreatedOrUpdated } from "../post-form.model";
import { validateFull } from "../../../components/Posts/PostForm/postFormValidator";
import {
  CLOSE_ALL_POST_FORMS,
  SAVE_POST_FORM_ROUTINE,
  UPDATE_POST_FORM_VALIDATION,
} from "../../../constants/ActionTypes";
import { processPostFormUpdate } from "../editing/model";
import { savePost } from "../post.model";
import { POST_STATES } from "../../../constants/PostsView";
import { uniq } from "lodash";
import {
  addPostForm,
  BULK_CREATE_POST_MODAL,
  bulkStartPostForm,
  closeBulkCreatePostModal,
  closePostFormInBulkCreate,
  ClosePostFormInBulkCreatePayload,
  duplicatePostFormInBulkCreate,
  DuplicatePostFormPayload,
  openBulkCreatePostModal,
  requestPostsFromClusterId,
  RequestPostsFromClusterId,
  requestPostsFromIds,
  RequestPostsFromIdsPayload,
  savePostFormInBulkCreate,
  SavePostFormPayload,
  setSaveData,
} from "./bulk-create-post.actions";
import {
  ActionOnCloseSelectedType,
  OpenBulkCreatePostModal,
} from "./bulk-create-post.types";
import {
  bulkModalPendingState,
  selectedPostIdSlice,
} from "./bulk-create-post.model";
import { TakeableChannel } from "redux-saga";
import { reloadPosts } from "../../../actions/postsView";
import {
  bulkCreatePostContextManager as bm,
  BulkCreatePostActiveContext,
} from "./bulk-create-post.active-context";
import { MaxFormCount } from "./constants";
import { toast } from "react-toastify";
import { EDIT_POST } from "../../../constants/Events";
import { getPage } from "../../../selectors/commonSelectors";
import { PAGES } from "../../../constants/Pages";
import { SaveFormResult } from "../../../components/Posts/PostForm/types";

function* handlePostRequests(
  requests: OpenBulkCreatePostModal["requests"],
  context: BulkCreatePostActiveContext
) {
  if (!requests?.length) {
    return;
  }

  const allAccounts: Record<string, AccountType> = yield select(
    (state: ReduxRootState) => state.entities.accounts.byId
  );
  const forms: Record<string, PostFormState> = yield select(
    (state: ReduxRootState) => state.forms.postForms.byId
  );

  const currentFormCount = Object.keys(forms).length;

  if (currentFormCount >= MaxFormCount) {
    console.warn(
      `Cannot open more than ${MaxFormCount} post forms at once. Current count: ${currentFormCount}`
    );
    return;
  }

  const validRequests = requests.slice(0, MaxFormCount - currentFormCount);
  if (requests.length > validRequests.length) {
    toast.info(`Maximum number of ${MaxFormCount} posts reached.`);
  }

  let lastPost: Post | undefined;
  for (const request of validRequests) {
    const accountIds = extractAccountIds(request.post);
    const accountsData = accountIds
      .map((id) => allAccounts[id as string])
      .filter(Boolean);
    let post = {
      ...request.post,
      _id: request.type === "create" ? generateNewPostId() : request.post._id,
      accountIds,
    } as Post;
    if (request.type === "create") {
      post = createPostNormalizer.makePostForCreate({
        post,
        assets: request.assets || [],
        accounts: accountsData,
        caption: undefined,
      });
    }
    if (forms[post._id]) {
      continue;
    }
    lastPost = post;
    const accountDisabled =
      request.type === "update" && request.accountDisabled;
    yield put(
      bulkStartPostForm({
        post,
        accounts: accountsData,
        requestType: request.type,
        accountSelectionDisabled: accountDisabled,
        form: request.form,
        requestFormTitle: request.formTitle,
      })
    );
    if (
      request.type === "update" &&
      request.saveBehavior?.type === "handled-by-callback"
    ) {
      context.registerFormSaveCallback(post._id, request.saveBehavior.callback);
    }
  }

  if (lastPost) {
    yield put(selectedPostIdSlice.actions.set(lastPost._id));
  }
}

function* bulkCreatePostModalSaga({
  payload,
}: {
  payload: OpenBulkCreatePostModal;
}) {
  const isOpened: boolean = yield select((state: ReduxRootState) =>
    isModalOpened(state, BULK_CREATE_POST_MODAL)
  );
  const context = isOpened ? bm.getActive() : bm.newActiveContext();

  yield handlePostRequests(payload.requests, context);

  if (isOpened) {
    return;
  }

  try {
    yield put(
      openModal({
        type: BULK_CREATE_POST_MODAL,
      })
    );

    yield take(closeBulkCreatePostModal.type);
    if (
      (!payload.postCloseBehavior ||
        payload.postCloseBehavior?.type === "auto-reload-page") &&
      context.isAnyFormSaved()
    ) {
      yield reloadCurrentPage(context);
    } else if (payload.postCloseBehavior?.type === "callback") {
      payload.postCloseBehavior.callback(context);
    }
  } catch (error) {
    console.error("Error closing bulk create post modal:", error);
  } finally {
    yield put({
      type: CLOSE_ALL_POST_FORMS,
    });
    yield put(bulkModalPendingState.actions.setLoading(false));
    yield put(
      closeModal({
        type: BULK_CREATE_POST_MODAL,
      })
    );
  }
}

function* reloadCurrentPage(context: BulkCreatePostActiveContext) {
  const page: string = yield select(getPage);
  if (page === PAGES.CALENDAR || page === PAGES.PLANNER) {
    postsCreatedOrUpdated({
      post: {
        newPosts: context.getAllSavedForms().map((c) => c.post),
      },
    });
    return;
  }
  yield put(reloadPosts());
}

function* addPostFormSaga({ payload }: { payload: OpenBulkCreatePostModal }) {
  yield handlePostRequests(payload.requests, bm.getActive());
}

function* formSaveSaga({ payload }: { payload: SavePostFormPayload }) {
  const { form: formResult, postSaveAction, requestType } = payload;
  const formState: PostFormState | undefined = yield select(
    (state: ReduxRootState) => state.forms.postForms.byId[formResult.post._id]
  );
  if (!formState) {
    console.error("Form state not found for post:", formResult.post._id);
    return;
  }

  yield put(isFormSubmitting.actions.startFormSubmitting());
  try {
    if (requestType === "create") {
      const createdPostIds: string[] | undefined = yield savePostForm({
        payload: { form: formResult, isControlled: true },
      });

      if (!createdPostIds) {
        return;
      }

      console.log("Post created:", createdPostIds, postSaveAction);

      if (postSaveAction === "save-and-close") {
        yield closeForm(formResult.post._id, {
          actionOnLastForm: "close-bulk-modal",
        });
        return;
      }

      yield handleSaveComplete({
        formState,
        reloadPostIds: createdPostIds,
        selectedPostId: createdPostIds[0],
        saveResult: formResult,
      });
    }
    if (requestType === "update") {
      const [errors, warnings] = validateFull(
        formResult.newPostState,
        formResult.post,
        formResult.platformFields,
        formResult.platforms,
        formState.overlapSchedule
      );

      if (errors && Object.keys(errors).length > 0) {
        yield put({
          type: UPDATE_POST_FORM_VALIDATION,
          payload: { postId: formResult.post._id, errors, warnings },
        });
        yield put({
          type: SAVE_POST_FORM_ROUTINE.FAILURE,
          error: false,
          payload: "",
        });
        return;
      }

      const allAccounts: Record<string, AccountType> = yield select(
        (state: ReduxRootState) => state.entities.accounts.byId
      );

      const { newPost, previousPost } = yield processPostFormUpdate({
        post: formState.requestedPost,
        formData: formResult,
        allAccounts,
      });

      const saveCallback = bm
        .getActive()
        .getFormSaveCallback(formState.post._id);

      if (saveCallback) {
        const posts = Array.isArray(newPost) ? newPost : [newPost];
        saveCallback({ post: posts[0], posts });
        yield closeForm(formResult.post._id, {
          actionOnLastForm: "close-bulk-modal",
        });
        return;
      }

      const result: Awaited<ReturnType<ReturnType<typeof savePost>>> =
        yield putResolve(savePost(newPost, previousPost));
      console.log("Post updated:", result, postSaveAction);

      if (postSaveAction === "save-and-close") {
        yield closeForm(formResult.post._id, {
          actionOnLastForm: "close-bulk-modal",
        });
        return;
      }

      yield handleSaveComplete({
        formState,
        reloadPostIds: result.ids,
        selectedPostId: result.ids[0],
        saveResult: formResult,
      });
    }
  } catch (error) {
    console.error("Error saving post form:", error);

    type ApiError = {
      response?: { data?: { error: string } };
    };
    yield put({
      type: SAVE_POST_FORM_ROUTINE.FAILURE,
      error: true,
      payload: {
        message: (error as ApiError)?.response?.data?.error || `${error}`,
        postId: formResult.post._id,
      },
    });
    toast.error((error as ApiError)?.response?.data?.error || `${error}`);
  } finally {
    yield put(isFormSubmitting.actions.finishFormSubmitting());
  }
}

function* handleSaveComplete({
  formState,
  reloadPostIds,
  saveResult,
}: {
  formState: PostFormState;
  reloadPostIds: string[];
  selectedPostId: string | "first-created" | undefined;
  saveResult: SaveFormResult;
}) {
  bm.getActive().notifyFormSaved(formState.post._id, saveResult);
  yield put(
    setSaveData({
      id: formState.post._id,
      data: {
        postIds: reloadPostIds,
        postState: formState.postState,
      },
    })
  );
}

function* closeForm(
  id: string,
  {
    actionOnLastForm,
    actionOnCloseSelected,
  }: {
    actionOnLastForm: "close-bulk-modal" | "none";
    actionOnCloseSelected?: ActionOnCloseSelectedType;
  }
) {
  const formIds: string[] = yield select((state: ReduxRootState) =>
    Object.keys(state.forms.postForms.byId)
  );
  yield put(closePostForm(id));
  if (
    actionOnLastForm === "close-bulk-modal" &&
    formIds.length === 1 &&
    formIds[0] === id
  ) {
    yield put(closeBulkCreatePostModal());
    return;
  }

  if (!actionOnCloseSelected || actionOnCloseSelected === "ignore") {
    return;
  }

  const selectedPostId: string | undefined = yield select(
    (state: ReduxRootState) =>
      state["@features/bulk-create-post"].selectedPostId
  );

  if (selectedPostId !== id) {
    return;
  }

  switch (actionOnCloseSelected) {
    case "select-first": {
      const firstPostId = formIds.find((postId) => postId !== id);
      yield put(selectedPostIdSlice.actions.set(firstPostId));

      break;
    }
    case "select-next": {
      const curPostIdx = formIds.indexOf(id);
      const nextPostId = formIds[(curPostIdx + 1) % formIds.length];
      yield put(selectedPostIdSlice.actions.set(nextPostId));

      break;
    }
    case "select-none": {
      yield put(selectedPostIdSlice.actions.set(undefined));
      break;
    }
    // No default
  }
}
export function* closePostFormInBulkCreateSaga({
  payload,
}: {
  payload: ClosePostFormInBulkCreatePayload;
}) {
  yield closeForm(payload.postId, {
    actionOnLastForm: "close-bulk-modal",
    actionOnCloseSelected: payload.actionOnCloseSelected,
  });
}

function* handleRequestPostsFormIds({
  payload,
}: {
  payload: RequestPostsFromIdsPayload;
}) {
  const { ids, state } = payload;
  yield put(bulkModalPendingState.actions.setLoading(true));
  yield put({
    type: openBulkCreatePostModal.type,
    payload: {},
  });
  try {
    const createdPosts: Post[] = yield Promise.all(
      uniq(ids).map(async (postId) => {
        if (state === POST_STATES.SCHEDULED) {
          return fetchActive(postId);
        }
        if (state === POST_STATES.QUEUED) {
          return fetchQueue(postId);
        }
        const draft = await fetchDraft(postId);
        return draft.schedule;
      })
    );

    for (const post of createdPosts) {
      yield addPostFormSaga({
        payload: {
          requests: [
            {
              type: "update",
              post: { ...post },
            },
          ],
        },
      });
    }
    yield put(selectedPostIdSlice.actions.set(ids[0]));
  } catch (error) {
    console.log(error);
  } finally {
    yield put(bulkModalPendingState.actions.setLoading(false));
  }
}

function* handleRequestPostsFormClusterId({
  payload,
}: {
  payload: RequestPostsFromClusterId;
}) {
  const { requestedPost, postCloseBehavior, trackEvent } = payload;
  const clusterId = requestedPost.postClusterId;

  if (!clusterId) {
    yield put({
      type: openBulkCreatePostModal.type,
      payload: {
        requests: [{ type: "update", post: requestedPost }],
        postCloseBehavior,
      } satisfies OpenBulkCreatePostModal,
    });
    trackEvent?.({
      eventName: EDIT_POST,
      metadata: { length: 1 },
    });
    return;
  }
  yield put(bulkModalPendingState.actions.setLoading(true));
  yield put({
    type: openBulkCreatePostModal.type,
    payload: {
      requests: [],
      postCloseBehavior,
    } satisfies OpenBulkCreatePostModal,
  });
  try {
    const allResult: Awaited<ReturnType<typeof fetchFromCluster>> =
      yield fetchFromCluster(clusterId);

    let allFetchedPosts = allResult.posts;
    const foundPostInArchive = allResult.archives.find(
      (a) => a._id === requestedPost._id
    );

    if (
      !allFetchedPosts.some((p) => p._id === requestedPost._id) &&
      foundPostInArchive
    ) {
      allFetchedPosts = [...allFetchedPosts, foundPostInArchive];
    }

    const createdPosts = orderSelectedPostOnTop(
      allFetchedPosts,
      requestedPost._id
    );

    for (const post of createdPosts) {
      yield addPostFormSaga({
        payload: {
          requests: [
            {
              type: "update",
              post: { ...post },
            },
          ],
        },
      });
    }

    yield put(selectedPostIdSlice.actions.set(requestedPost._id));
    if (createdPosts.length > MaxFormCount) {
      toast.info(`Maximum number of ${MaxFormCount} posts reached.`);
    }
    trackEvent?.({
      eventName: EDIT_POST,
      metadata: {
        length: Math.min(createdPosts.length, MaxFormCount),
        "length-cluster": createdPosts.length,
      },
    });
  } catch (error) {
    console.log(error);
    yield put(bulkModalPendingState.actions.setError(`${error}`));
  } finally {
    yield put(bulkModalPendingState.actions.setLoading(false));
  }
}

export function orderSelectedPostOnTop<T extends Pick<Post, "_id">>(
  posts: T[],
  selectedId: string | undefined
) {
  const index = posts.findIndex((p) => p._id === selectedId);
  if (index === -1 || index === 0) {
    return posts;
  }

  const selectedPost = posts[index];
  const newPosts = [...posts.slice(0, index), ...posts.slice(index + 1)];
  newPosts.unshift(selectedPost);
  return newPosts;
}

function* duplicatePostFormInBulkCreateSaga({
  payload,
}: {
  payload: DuplicatePostFormPayload;
}) {
  const { fromId } = payload;
  const allForms: Record<string, PostFormState> = yield select(
    (state: ReduxRootState) => state.forms.postForms.byId
  );
  const formState = allForms[fromId];
  if (!formState) {
    console.error("Form state not found for post:", fromId);
    return;
  }

  yield handlePostRequests(
    [
      {
        type: "create",
        post: {
          ...formState.post,
          _id: undefined,
          postType: undefined,
        },
        form: {
          accounts: formState.accounts,
          platforms: formState.platforms,
          platformFields: formState.platformFields,
          view: formState.view,
          accountSelectionDisabled: formState.accountSelectionDisabled,
          postState: formState.postState,
        },
      },
    ],
    bm.getActive()
  );
}

async function fetchFromCluster(
  id: string
): Promise<{ posts: Post[]; archives: Post[] }> {
  const rs = await axios.get<{
    drafts: LoadedDraft[];
    queues: Post[];
    upcomings: Post[];
    archives: Post[];
  }>(`/schedules/cluster/${id}`);
  const normalizedDrafts = await Promise.all(
    rs.data.drafts.map((draft) => normalizeDraft(draft))
  );

  /** ignore archives */
  return {
    posts: [
      ...normalizedDrafts.map((c) => c.schedule),
      ...(await standardizationUpcomingPosts(rs.data.queues, [])),
      ...(await standardizationUpcomingPosts(rs.data.upcomings, [])),
    ],
    archives: await Promise.all(
      (rs.data.archives || []).map(async (post) =>
        standardizationPostData(post)
      )
    ),
  };
}

async function fetchDraft(id: string) {
  const rs = await axios.get<LoadedDraft>(`/schedules/drafts/${id}`);
  return await normalizeDraft(rs.data);
}

async function fetchActive(id: string) {
  const rs = await axios.get<Post>(`/posts/${id}/upcoming`);
  const upcomings = await standardizationUpcomingPosts([rs.data], []);
  return upcomings[0];
}
async function fetchQueue(id: string) {
  const rs = await axios.get<Post>(`/posts/${id}/queue`);
  const upcomings = await standardizationUpcomingPosts([rs.data], []);
  return upcomings[0];
}

export function* createBulkPostSaga() {
  yield takeEvery(
    openBulkCreatePostModal.type as unknown as TakeableChannel<unknown>,
    bulkCreatePostModalSaga
  );
  yield takeEvery(
    addPostForm.type as unknown as TakeableChannel<unknown>,
    addPostFormSaga
  );
  yield takeEvery(
    savePostFormInBulkCreate.type as unknown as TakeableChannel<unknown>,
    formSaveSaga
  );
  yield takeEvery(
    closePostFormInBulkCreate.type as unknown as TakeableChannel<unknown>,
    closePostFormInBulkCreateSaga
  );
  yield takeEvery(
    requestPostsFromIds.type as unknown as TakeableChannel<unknown>,
    handleRequestPostsFormIds
  );
  yield takeEvery(
    requestPostsFromClusterId.type as unknown as TakeableChannel<unknown>,
    handleRequestPostsFormClusterId
  );
  yield takeEvery(
    duplicatePostFormInBulkCreate.type as unknown as TakeableChannel<unknown>,
    duplicatePostFormInBulkCreateSaga
  );
}

function extractAccountIds(post: Partial<Post> | undefined): string[] {
  if (!post) {
    return [];
  }
  if (post.accountIds?.length) {
    return post.accountIds;
  }
  if (post.accountId) {
    return [post.accountId];
  }
  return [];
}
